// pages/StoreDetailPage.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  Chip,
  Skeleton,
  Paper,
  Avatar,
  IconButton,
  Divider,
  Stack,
  Tooltip,
  Rating,
  Dialog,
  IconButton as MuiIconButton,
  Snackbar,
  Alert,
  Zoom,
  Fade,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  ContentCopy as CopyIcon,
  MenuBook as MenuBookIcon,
  Share as ShareIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  AccessTime as TimeIcon,
  Star as StarIcon,
  LocalOffer as OfferIcon,
  LocalShipping as DeliveryIcon,
  Verified as VerifiedIcon,
  Storefront as StorefrontIcon,
  Restaurant as RestaurantIcon,
  LocalCafe as CafeIcon,
  Directions as DirectionsIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ZoomIn as ZoomInIcon,
  Navigation as NavigationIcon,
  Store as StoreFallbackIcon,
  WhatsApp as WhatsAppIcon,
  Launch as LaunchIcon,
  PhotoLibrary as PhotoLibraryIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';
import noPhoto from '../assets/no_photo.webp';
import currencyTrans from '../components/CarrancyTrans';

/* ============================================================
   StoreDetailPage
   ============================================================ */
const StoreDetailPage = ({ keywordsData, language }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isRtl = language === 'ar';

  // Design tokens
  const goldAccent = '#E6B800';
  const goldBright = '#FFD700';
  const softGold = 'rgba(230, 184, 0, 0.12)';
  const surface = 'rgba(20, 20, 20, 0.75)';
  const cardBg = 'rgba(255, 255, 255, 0.025)';
  const border = 'rgba(230, 184, 0, 0.15)';

  // State
  const [place, setPlace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const heroRef = useRef(null);

  // ---------- Fetch place ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/tour/places/${id}`);
        if (!cancelled) setPlace(res.data?.place || null);
      } catch (err) {
        console.error('Failed to load place:', err);
        if (!cancelled) setError(err.response?.data?.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // ---------- Load favorite status ----------
  useEffect(() => {
    try {
      const favs = JSON.parse(localStorage.getItem('tour_favorites') || '[]');
      setIsFavorite(favs.includes(Number(id)));
    } catch {}
  }, [id]);

  const toggleFavorite = () => {
    try {
      const favs = JSON.parse(localStorage.getItem('tour_favorites') || '[]');
      const numericId = Number(id);
      const next = favs.includes(numericId)
        ? favs.filter((f) => f !== numericId)
        : [...favs, numericId];
      localStorage.setItem('tour_favorites', JSON.stringify(next));
      setIsFavorite(next.includes(numericId));
    } catch {}
  };

  // ---------- Build image gallery ----------
  const galleryImages = useMemo(() => {
    if (!place) return [];
    const imgs = [];
    if (place.cover_image) imgs.push(place.cover_image);
    if (place.logo && place.logo !== place.cover_image) imgs.push(place.logo);
    if (Array.isArray(place.gallery)) imgs.push(...place.gallery);
    if (Array.isArray(place.images)) imgs.push(...place.images);
    return [...new Set(imgs.filter(Boolean))];
  }, [place]);

  const heroImage = galleryImages[0] || noPhoto;

  // ---------- Copy to clipboard ----------
  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar({
        open: true,
        message: isRtl ? `تم نسخ ${label}` : `${label} copied`,
        severity: 'success',
      });
    } catch {
      setSnackbar({
        open: true,
        message: isRtl ? 'تعذّر النسخ' : 'Copy failed',
        severity: 'error',
      });
    }
  };

  // ---------- Share ----------
  const share = async () => {
    const url = window.location.href;
    const title = isRtl ? place?.name : place?.name_en || place?.name;
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch {}
    } else {
      copy(url, isRtl ? 'الرابط' : 'Link');
    }
  };

  // ---------- Maps links ----------
  const fullAddress = isRtl ? place?.address : place?.address_en || place?.address;
  const mapEmbedUrl = useMemo(() => {
    if (!place?.latitude || !place?.longitude) return null;
    const lat = place.latitude;
    const lng = place.longitude;
    // Standard Google Maps embed (no API key needed)
    return `https://www.google.com/maps?q=${lat},${lng}&hl=${language}&z=17&output=embed`;
  }, [place, language]);

  const directionsUrl = useMemo(() => {
    if (!place) return null;
    if (place.latitude && place.longitude) {
      return `https://www.google.com/maps/dir/?api=1&destination=${place.latitude},${place.longitude}`;
    }
    if (fullAddress) {
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`;
    }
    return null;
  }, [place, fullAddress]);

  // ---------- Phone formatting ----------
  const cleanPhone = (phone) => (phone ? String(phone).replace(/[^\d+]/g, '') : '');
  const whatsappPhone = (phone) => (phone ? String(phone).replace(/[^\d]/g, '') : '');

  // ---------- Type icon ----------
  const TypeIcon = useMemo(() => {
    switch (place?.type || place?.cuisine_type) {
      case 'Restaurant': return RestaurantIcon;
      case 'Cafe': return CafeIcon;
      case 'Super market': return StorefrontIcon;
      default: return StorefrontIcon;
    }
  }, [place]);

  const typeLabel = useMemo(() => {
    const t = place?.type || place?.cuisine_type;
    if (isRtl) {
      return t === 'Restaurant' ? 'مطعم' : t === 'Cafe' ? 'مقهى' : t === 'Super market' ? 'متجر' : 'مكان';
    }
    return t === 'Restaurant' ? 'Restaurant' : t === 'Cafe' ? 'Cafe' : t === 'Super market' ? 'Store' : 'Place';
  }, [place, isRtl]);

  // ---------- Loading skeleton ----------
  if (loading) {
    return (
      <Box sx={{ bgcolor: '#0A0A0A', minHeight: '100vh', pb: { xs: 12, md: 14 } }}>
        <Skeleton variant="rectangular" sx={{ height: { xs: 260, sm: 340, md: 420 }, bgcolor: 'rgba(255,255,255,0.03)' }} />
        <Container maxWidth="lg" sx={{ mt: 3, px: { xs: 2, sm: 3, md: 4 } }}>
          <Skeleton variant="text" width="60%" sx={{ bgcolor: 'rgba(255,255,255,0.05)', height: 40 }} />
          <Skeleton variant="text" width="40%" sx={{ bgcolor: 'rgba(255,255,255,0.05)', mb: 3 }} />
          <Skeleton variant="rectangular" sx={{ height: 200, borderRadius: '20px', bgcolor: 'rgba(255,255,255,0.03)' }} />
        </Container>
      </Box>
    );
  }

  // ---------- Error / not found ----------
  if (error || !place) {
    return (
      <Box
        sx={{
          bgcolor: '#0A0A0A',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          pb: { xs: 14, md: 16 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, sm: 6 },
            textAlign: 'center',
            bgcolor: cardBg,
            border: `1px dashed rgba(255,255,255,0.08)`,
            borderRadius: '22px',
            maxWidth: 480,
          }}
        >
          <StoreFallbackIcon sx={{ fontSize: 64, color: 'rgba(255,255,255,0.15)', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
            {isRtl ? 'المكان غير موجود' : 'Place not found'}
          </Typography>
          <Typography sx={{ color: '#8A8A8A', fontSize: '0.85rem', mb: 3 }}>
            {isRtl
              ? 'قد يكون الرابط قديماً أو أن المكان لم يعد متاحاً.'
              : 'The link may be outdated or the place is no longer available.'}
          </Typography>
          <Button
            onClick={() => navigate('/tour-guide')}
            startIcon={isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
            sx={{
              background: `linear-gradient(135deg, ${goldAccent}, ${goldBright})`,
              color: '#111',
              fontWeight: 800,
              px: 3,
              py: 1.2,
              borderRadius: '999px',
              textTransform: 'none',
              '&:hover': { transform: 'translateY(-2px)' },
            }}
          >
            {isRtl ? 'العودة للدليل' : 'Back to guide'}
          </Button>
        </Paper>
      </Box>
    );
  }

  /* ============================================================
     RENDER
     ============================================================ */
  return (
    <>
      <Helmet htmlAttributes={{ lang: isRtl ? 'ar' : 'en', dir: isRtl ? 'rtl' : 'ltr' }}>
        <title>{(isRtl ? place.name : place.name_en || place.name) + ' — QR Table'}</title>
        <meta name="description" content={(isRtl ? place.description : place.description_en || place.description) || ''} />
      </Helmet>

      <Box
        dir={isRtl ? 'rtl' : 'ltr'}
        sx={{
          bgcolor: '#0A0A0A',
          minHeight: '100vh',
          pb: { xs: 14, md: 16 },
          background: `
            radial-gradient(circle at 10% 0%, rgba(230,184,0,0.05) 0%, transparent 45%),
            radial-gradient(circle at 90% 100%, rgba(230,184,0,0.03) 0%, transparent 45%),
            #0A0A0A
          `,
        }}
      >
        {/* ============ HERO IMAGE ============ */}
        <Box
          ref={heroRef}
          sx={{
            position: 'relative',
            height: { xs: '55vh', sm: '60vh', md: '68vh' },
            minHeight: { xs: 320, sm: 380, md: 460 },
            maxHeight: { xs: 520, md: 700 },
            overflow: 'hidden',
            bgcolor: '#111',
          }}
        >
          {!heroLoaded && (
            <Skeleton variant="rectangular" sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.03)' }} />
          )}

          <motion.div
            initial={{ scale: 1.06, opacity: 0 }}
            animate={{ scale: 1, opacity: heroLoaded ? 1 : 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            style={{ width: '100%', height: '100%' }}
          >
            <Box
              component="img"
              src={heroImage}
              alt={place.name}
              onLoad={() => setHeroLoaded(true)}
              onError={(e) => { e.currentTarget.src = noPhoto; setHeroLoaded(true); }}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                // Perf hint for large 4K images
                imageRendering: '-webkit-optimize-contrast',
              }}
            />
          </motion.div>

          {/* Gradient overlays for readability */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: `
                linear-gradient(180deg,
                  rgba(0,0,0,0.55) 0%,
                  rgba(0,0,0,0.15) 30%,
                  rgba(0,0,0,0.15) 60%,
                  rgba(10,10,10,0.95) 100%
                )
              `,
              pointerEvents: 'none',
            }}
          />

          {/* Top bar: back / share / favorite */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              p: { xs: 2, sm: 3 },
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              zIndex: 3,
            }}
          >
            <IconButton
              onClick={() => navigate(-1)}
              aria-label={isRtl ? 'رجوع' : 'Back'}
              sx={{
                bgcolor: 'rgba(0,0,0,0.55)',
                color: '#fff',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
              }}
            >
              {isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
            </IconButton>

            <Stack direction="row" spacing={1}>
              <IconButton
                onClick={share}
                aria-label={isRtl ? 'مشاركة' : 'Share'}
                sx={{
                  bgcolor: 'rgba(0,0,0,0.55)',
                  color: '#fff',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                }}
              >
                <ShareIcon />
              </IconButton>
              <IconButton
                onClick={toggleFavorite}
                aria-label={isRtl ? 'حفظ' : 'Save'}
                sx={{
                  bgcolor: 'rgba(0,0,0,0.55)',
                  color: isFavorite ? '#FF5252' : '#fff',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                }}
              >
                {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
              </IconButton>
            </Stack>
          </Box>

          {/* Bottom overlay: name + badges */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              p: { xs: 2, sm: 3, md: 4 },
              zIndex: 2,
            }}
          >
            <Container maxWidth="lg" disableGutters>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  mb: 1.5,
                  flexWrap: 'wrap',
                  gap: 1,
                  flexDirection: isRtl ? 'row-reverse' : 'row',
                  justifyContent: isRtl ? 'flex-end' : 'flex-start',
                }}
              >
                <Chip
                  icon={<TypeIcon sx={{ fontSize: '1rem !important', color: '#111 !important' }} />}
                  label={typeLabel}
                  size="small"
                  sx={{
                    bgcolor: goldBright,
                    color: '#111',
                    fontWeight: 800,
                    fontSize: '0.72rem',
                    height: 26,
                    '& .MuiChip-icon': { color: '#111' },
                  }}
                />
                {place.verified && (
                  <Chip
                    icon={<VerifiedIcon sx={{ fontSize: '1rem !important', color: '#111 !important' }} />}
                    label={isRtl ? 'موثّق' : 'Verified'}
                    size="small"
                    sx={{
                      bgcolor: '#fff',
                      color: '#111',
                      fontWeight: 800,
                      fontSize: '0.72rem',
                      height: 26,
                    }}
                  />
                )}
                {place.featured && (
                  <Chip
                    icon={<StarIcon sx={{ fontSize: '1rem !important', color: '#111 !important' }} />}
                    label={isRtl ? 'مميز' : 'Featured'}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.9)',
                      color: '#111',
                      fontWeight: 800,
                      fontSize: '0.72rem',
                      height: 26,
                    }}
                  />
                )}
                <Chip
                  label={place.is_open ? (isRtl ? 'مفتوح الآن' : 'Open now') : (isRtl ? 'مغلق' : 'Closed')}
                  size="small"
                  sx={{
                    bgcolor: place.is_open ? '#4CAF50' : '#FF5252',
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: '0.72rem',
                    height: 26,
                  }}
                />
              </Stack>

              <Typography
                component="h1"
                sx={{
                  color: '#fff',
                  fontWeight: 900,
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.6rem' },
                  lineHeight: 1.15,
                  letterSpacing: -0.5,
                  mb: 0.75,
                  textShadow: '0 2px 20px rgba(0,0,0,0.6)',
                }}
              >
                {isRtl ? place.name : place.name_en || place.name}
              </Typography>

              <Stack
                direction="row"
                spacing={{ xs: 1.5, sm: 2.5 }}
                alignItems="center"
                flexWrap="wrap"
                sx={{ flexDirection: isRtl ? 'row-reverse' : 'row' }}
              >
                {place.rating > 0 && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <StarIcon sx={{ color: goldBright, fontSize: 18 }} />
                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>
                      {Number(place.rating).toFixed(1)}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>
                      ({place.reviews_count || place.total_reviews || 0})
                    </Typography>
                  </Stack>
                )}
                {place.cuisine_type && (
                  <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem' }}>
                    {place.cuisine_type}
                  </Typography>
                )}
                {place.city && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <LocationIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 16 }} />
                    <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem' }}>
                      {place.city}
                      {place.area ? `, ${place.area}` : ''}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Container>
          </Box>

          {/* Gallery indicator + expand */}
          {galleryImages.length > 1 && (
            <Fade in>
              <Button
                onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}
                startIcon={<PhotoLibraryIcon />}
                sx={{
                  position: 'absolute',
                  bottom: { xs: 120, sm: 130, md: 150 },
                  [isRtl ? 'left' : 'right']: { xs: 16, sm: 24 },
                  zIndex: 3,
                  bgcolor: 'rgba(0,0,0,0.55)',
                  color: '#fff',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '999px',
                  px: 2,
                  py: 0.8,
                  textTransform: 'none',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.75)', transform: 'translateY(-1px)' },
                }}
              >
                {galleryImages.length} {isRtl ? 'صورة' : 'photos'}
              </Button>
            </Fade>
          )}
        </Box>

        {/* ============ CONTENT ============ */}
        <Container maxWidth="lg" sx={{ mt: { xs: -2, md: -3 }, px: { xs: 2, sm: 3, md: 4 }, position: 'relative', zIndex: 4 }}>
          <Grid container spacing={{ xs: 2.5, md: 3 }}>
            {/* LEFT: Main info */}
            <Grid item xs={12} md={8}>
              {/* Quick actions */}
              <Card
                sx={{
                  bgcolor: cardBg,
                  backdropFilter: 'blur(14px)',
                  border: `1px solid rgba(255,255,255,0.06)`,
                  borderRadius: { xs: '18px', sm: '20px' },
                  mb: 3,
                  overflow: 'visible',
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Grid container spacing={1.5}>
                    {/* Call */}
                    {place.phone && (
                      <Grid item xs={6} sm={3}>
                        <QuickAction
                          icon={<PhoneIcon />}
                          label={isRtl ? 'اتصال' : 'Call'}
                          onClick={() => window.location.href = `tel:${cleanPhone(place.phone)}`}
                          goldAccent={goldAccent}
                          goldBright={goldBright}
                        />
                      </Grid>
                    )}

                    {/* WhatsApp */}
                    {place.phone && (
                      <Grid item xs={6} sm={3}>
                        <QuickAction
                          icon={<WhatsAppIcon />}
                          label="WhatsApp"
                          onClick={() => window.open(`https://wa.me/${whatsappPhone(place.phone)}`, '_blank', 'noopener,noreferrer')}
                          goldAccent={goldAccent}
                          goldBright={goldBright}
                          color="#25D366"
                        />
                      </Grid>
                    )}

                    {/* Directions */}
                    {directionsUrl && (
                      <Grid item xs={6} sm={3}>
                        <QuickAction
                          icon={<DirectionsIcon />}
                          label={isRtl ? 'الاتجاهات' : 'Directions'}
                          onClick={() => window.open(directionsUrl, '_blank', 'noopener,noreferrer')}
                          goldAccent={goldAccent}
                          goldBright={goldBright}
                        />
                      </Grid>
                    )}

                    {/* Copy address */}
                    {fullAddress && (
                      <Grid item xs={6} sm={3}>
                        <QuickAction
                          icon={<CopyIcon />}
                          label={isRtl ? 'نسخ العنوان' : 'Copy address'}
                          onClick={() => copy(fullAddress, isRtl ? 'العنوان' : 'Address')}
                          goldAccent={goldAccent}
                          goldBright={goldBright}
                        />
                      </Grid>
                    )}
                    <Grid item xs={6} sm={3}>
                        <QuickAction
                          icon={<MenuBookIcon />}
                          label={isRtl ? 'عرض المنيو' : 'Menu'}
                          onClick={() => navigate(`/restaurant/${place.id}`)}
                          goldAccent={goldAccent}
                          goldBright={goldBright}
                        />
                      </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* About */}
              {(place.description || place.description_en) && (
                <SectionCard title={isRtl ? 'عن المكان' : 'About'} goldBright={goldBright} cardBg={cardBg}>
                  <Typography
                    sx={{
                      color: '#D0D0D0',
                      fontSize: { xs: '0.9rem', sm: '0.95rem' },
                      lineHeight: 1.8,
                      textAlign: isRtl ? 'right' : 'left',
                    }}
                  >
                    {isRtl ? place.description : place.description_en || place.description}
                  </Typography>
                </SectionCard>
              )}

              {/* Contact info */}
              <SectionCard title={isRtl ? 'معلومات التواصل' : 'Contact information'} goldBright={goldBright} cardBg={cardBg}>
                <Stack spacing={2}>
                  {place.phone && (
                    <InfoRow
                      icon={<PhoneIcon />}
                      label={isRtl ? 'الهاتف' : 'Phone'}
                      value={place.phone}
                      action={
                        <IconButton
                          size="small"
                          onClick={() => copy(place.phone, isRtl ? 'الهاتف' : 'Phone')}
                          sx={{ color: goldBright, '&:hover': { bgcolor: softGold } }}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      }
                      goldBright={goldBright}
                      isRtl={isRtl}
                    />
                  )}

                  {fullAddress && (
                    <InfoRow
                      icon={<LocationIcon />}
                      label={isRtl ? 'العنوان' : 'Address'}
                      value={fullAddress}
                      action={
                        <IconButton
                          size="small"
                          onClick={() => copy(fullAddress, isRtl ? 'العنوان' : 'Address')}
                          sx={{ color: goldBright, '&:hover': { bgcolor: softGold } }}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      }
                      goldBright={goldBright}
                      isRtl={isRtl}
                    />
                  )}

                  {/* Hours */}
                  {place.opening_hours && Object.keys(place.opening_hours).length > 0 && (
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                        <TimeIcon sx={{ color: goldBright, fontSize: 20 }} />
                        <Typography sx={{ color: '#8A8A8A', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {isRtl ? 'ساعات العمل' : 'Opening hours'}
                        </Typography>
                      </Stack>
                      <Box
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.02)',
                          border: `1px solid rgba(255,255,255,0.05)`,
                          borderRadius: '14px',
                          p: 1.5,
                        }}
                      >
                        {Object.entries(place.opening_hours).map(([day, hours]) => {
                          const dayLabels = {
                            mon: isRtl ? 'الإثنين' : 'Mon',
                            tue: isRtl ? 'الثلاثاء' : 'Tue',
                            wed: isRtl ? 'الأربعاء' : 'Wed',
                            thu: isRtl ? 'الخميس' : 'Thu',
                            fri: isRtl ? 'الجمعة' : 'Fri',
                            sat: isRtl ? 'السبت' : 'Sat',
                            sun: isRtl ? 'الأحد' : 'Sun',
                          };
                          const isClosed = hours?.closed === true || hours?.is_closed;
                          return (
                            <Stack
                              key={day}
                              direction="row"
                              justifyContent="space-between"
                              sx={{ py: 0.75, flexDirection: isRtl ? 'row-reverse' : 'row' }}
                            >
                              <Typography sx={{ color: '#B8B8B8', fontSize: '0.85rem', fontWeight: 600 }}>
                                {dayLabels[day] || day}
                              </Typography>
                              <Typography
                                sx={{
                                  color: isClosed ? '#FF5252' : '#fff',
                                  fontSize: '0.85rem',
                                  fontWeight: 600,
                                  direction: 'ltr',
                                }}
                              >
                                {isClosed
                                  ? (isRtl ? 'مغلق' : 'Closed')
                                  : `${hours?.open || '—'} – ${hours?.close || '—'}`}
                              </Typography>
                            </Stack>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Stack>
              </SectionCard>

              {/* Delivery info */}
              {(place.delivery_fee > 0 || place.delivery_time_min || place.min_order_amount > 0) && (
                <SectionCard title={isRtl ? 'معلومات التوصيل' : 'Delivery info'} goldBright={goldBright} cardBg={cardBg}>
                  <Grid container spacing={2}>
                    {place.delivery_time_min && (
                      <Grid item xs={6} sm={4}>
                        <StatBox
                          icon={<TimeIcon />}
                          label={isRtl ? 'وقت التوصيل' : 'Delivery time'}
                          value={`${place.delivery_time_min} ${isRtl ? 'دقيقة' : 'min'}`}
                          goldBright={goldBright}
                        />
                      </Grid>
                    )}
                    {place.delivery_fee !== undefined && (
                      <Grid item xs={6} sm={4}>
                        <StatBox
                          icon={<DeliveryIcon />}
                          label={isRtl ? 'رسوم التوصيل' : 'Delivery fee'}
                          value={`${place.delivery_fee} ${currencyTrans(place.currency || 'SYP', language)}`}
                          goldBright={goldBright}
                        />
                      </Grid>
                    )}
                    {place.min_order_amount !== undefined && (
                      <Grid item xs={6} sm={4}>
                        <StatBox
                          icon={<OfferIcon />}
                          label={isRtl ? 'الحد الأدنى' : 'Min order'}
                          value={`${place.min_order_amount} ${currencyTrans(place.currency || 'SYP', language)}`}
                          goldBright={goldBright}
                        />
                      </Grid>
                    )}
                  </Grid>
                </SectionCard>
              )}
            </Grid>

            {/* RIGHT: Map + gallery + sticky info */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  position: { md: 'sticky' },
                  top: { md: 90 },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2.5,
                }}
              >
                {/* Map */}
                {mapEmbedUrl && (
                  <Card
                    sx={{
                      bgcolor: cardBg,
                      border: `1px solid rgba(255,255,255,0.06)`,
                      borderRadius: '20px',
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{ position: 'relative', height: { xs: 220, sm: 260 }, bgcolor: '#111' }}>
                      <iframe
                        src={mapEmbedUrl}
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        loading="lazy"
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                        title={`${place.name} — map`}
                      />
                    </Box>
                    <Box sx={{ p: 1.5 }}>
                      <Button
                        fullWidth
                        onClick={() => window.open(directionsUrl, '_blank', 'noopener,noreferrer')}
                        startIcon={<NavigationIcon />}
                        sx={{
                          background: `linear-gradient(135deg, ${goldAccent}, ${goldBright})`,
                          color: '#111',
                          fontWeight: 800,
                          py: 1.2,
                          borderRadius: '14px',
                          textTransform: 'none',
                          fontSize: '0.85rem',
                          boxShadow: '0 8px 20px rgba(230,184,0,0.25)',
                          '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 12px 28px rgba(230,184,0,0.4)' },
                        }}
                      >
                        {isRtl ? 'احصل على الاتجاهات' : 'Get directions'}
                      </Button>
                    </Box>
                  </Card>
                )}

                {/* Gallery thumbnails */}
                {galleryImages.length > 1 && (
                  <Card
                    sx={{
                      bgcolor: cardBg,
                      border: `1px solid rgba(255,255,255,0.06)`,
                      borderRadius: '20px',
                      p: 2,
                    }}
                  >
                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem', mb: 1.5, textAlign: isRtl ? 'right' : 'left' }}>
                      {isRtl ? 'معرض الصور' : 'Gallery'}
                    </Typography>
                    <Grid container spacing={1}>
                      {galleryImages.slice(0, 6).map((img, i) => (
                        <Grid item xs={4} key={i}>
                          <Box
                            onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }}
                            sx={{
                              position: 'relative',
                              paddingTop: '100%',
                              borderRadius: '12px',
                              overflow: 'hidden',
                              cursor: 'pointer',
                              bgcolor: 'rgba(255,255,255,0.03)',
                              '&:hover img': { transform: 'scale(1.08)' },
                              '&:hover .zoom-overlay': { opacity: 1 },
                            }}
                          >
                            <Box
                              component="img"
                              src={img}
                              alt={`${place.name} ${i + 1}`}
                              loading="lazy"
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                transition: 'transform 0.4s ease',
                              }}
                            />
                            <Box
                              className="zoom-overlay"
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                bgcolor: 'rgba(0,0,0,0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: 0,
                                transition: 'opacity 0.2s ease',
                              }}
                            >
                              <ZoomInIcon sx={{ color: '#fff', fontSize: 22 }} />
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Card>
                )}

                {/* Mini contact card */}
                <Card
                  sx={{
                    bgcolor: cardBg,
                    border: `1px solid rgba(255,255,255,0.06)`,
                    borderRadius: '20px',
                    p: 2,
                  }}
                >
                  <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem', mb: 1.5, textAlign: isRtl ? 'right' : 'left' }}>
                    {isRtl ? 'تواصل سريع' : 'Quick contact'}
                  </Typography>
                  <Stack spacing={1}>
                    {place.phone && (
                      <Button
                        fullWidth
                        startIcon={<PhoneIcon />}
                        onClick={() => window.location.href = `tel:${cleanPhone(place.phone)}`}
                        sx={{
                          justifyContent: 'flex-start',
                          color: '#fff',
                          bgcolor: 'rgba(255,255,255,0.03)',
                          border: `1px solid rgba(255,255,255,0.06)`,
                          borderRadius: '12px',
                          py: 1.2,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          direction: 'ltr',
                          '&:hover': { bgcolor: softGold, borderColor: border },
                        }}
                      >
                        {place.phone}
                      </Button>
                    )}
                    {fullAddress && (
                      <Button
                        fullWidth
                        startIcon={<LocationIcon />}
                        onClick={() => window.open(directionsUrl, '_blank', 'noopener,noreferrer')}
                        sx={{
                          justifyContent: 'flex-start',
                          color: '#fff',
                          bgcolor: 'rgba(255,255,255,0.03)',
                          border: `1px solid rgba(255,255,255,0.06)`,
                          borderRadius: '12px',
                          py: 1.2,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.82rem',
                          textAlign: isRtl ? 'right' : 'left',
                          '&:hover': { bgcolor: softGold, borderColor: border },
                        }}
                      >
                        <Typography
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '0.82rem',
                          }}
                        >
                          {fullAddress}
                        </Typography>
                      </Button>
                    )}
                  </Stack>
                </Card>
              </Box>
            </Grid>
          </Grid>
        </Container>

        {/* ============ STICKY MOBILE BOTTOM BAR ============ */}
        <Box
          sx={{
            position: 'fixed',
            bottom: { xs: 72, sm: 78, md: 86 },
            left: 0,
            right: 0,
            display: { xs: 'block', md: 'none' },
            zIndex: 1100,
            pointerEvents: 'none',
          }}
        >
          <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
            <Paper
              elevation={0}
              sx={{
                pointerEvents: 'auto',
                p: 1,
                bgcolor: 'rgba(20,20,20,0.92)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: `1px solid rgba(230,184,0,0.25)`,
                borderRadius: '16px',
                boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
              }}
            >
              <Stack direction="row" spacing={1}>
                {place.phone && (
                  <Button
                    fullWidth
                    startIcon={<PhoneIcon />}
                    onClick={() => window.location.href = `tel:${cleanPhone(place.phone)}`}
                    sx={{
                      background: `linear-gradient(135deg, ${goldAccent}, ${goldBright})`,
                      color: '#111',
                      fontWeight: 800,
                      py: 1.1,
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontSize: '0.85rem',
                    }}
                  >
                    {isRtl ? 'اتصال' : 'Call'}
                  </Button>
                )}
                {directionsUrl && (
                  <Button
                    fullWidth
                    startIcon={<NavigationIcon />}
                    onClick={() => window.open(directionsUrl, '_blank', 'noopener,noreferrer')}
                    sx={{
                      color: goldBright,
                      border: `1px solid ${border}`,
                      bgcolor: 'rgba(255,255,255,0.03)',
                      fontWeight: 800,
                      py: 1.1,
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontSize: '0.85rem',
                      '&:hover': { bgcolor: softGold },
                    }}
                  >
                    {isRtl ? 'الاتجاهات' : 'Directions'}
                  </Button>
                )}
              </Stack>
            </Paper>
          </Container>
        </Box>
      </Box>

      {/* ============ LIGHTBOX ============ */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0,0,0,0.95)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${border}`,
            borderRadius: '20px',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ position: 'relative', bgcolor: '#000' }}>
          {/* Close */}
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{
              position: 'absolute',
              top: 12,
              [isRtl ? 'left' : 'right']: 12,
              zIndex: 3,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Counter */}
          <Box
            sx={{
              position: 'absolute',
              top: 18,
              [isRtl ? 'right' : 'left']: 16,
              zIndex: 3,
              px: 1.5,
              py: 0.5,
              borderRadius: '999px',
              bgcolor: 'rgba(0,0,0,0.6)',
              color: '#fff',
              fontSize: '0.78rem',
              fontWeight: 700,
            }}
          >
            {lightboxIndex + 1} / {galleryImages.length}
          </Box>

          {/* Image */}
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: { xs: '60vh', md: '75vh' },
              maxHeight: '85vh',
              bgcolor: '#000',
            }}
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={lightboxIndex}
                src={galleryImages[lightboxIndex]}
                alt=""
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.02 }}
                transition={{ duration: 0.3 }}
                style={{
                  maxWidth: '100%',
                  maxHeight: '85vh',
                  objectFit: 'contain',
                }}
              />
            </AnimatePresence>

            {/* Prev / Next */}
            {galleryImages.length > 1 && (
              <>
                <IconButton
                  onClick={() =>
                    setLightboxIndex((i) => (i - 1 + galleryImages.length) % galleryImages.length)
                  }
                  sx={{
                    position: 'absolute',
                    [isRtl ? 'right' : 'left']: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    backdropFilter: 'blur(12px)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                  }}
                >
                  {isRtl ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </IconButton>
                <IconButton
                  onClick={() => setLightboxIndex((i) => (i + 1) % galleryImages.length)}
                  sx={{
                    position: 'absolute',
                    [isRtl ? 'left' : 'right']: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    backdropFilter: 'blur(12px)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                  }}
                >
                  {isRtl ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                </IconButton>
              </>
            )}
          </Box>

          {/* Thumbnails strip */}
          {galleryImages.length > 1 && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                p: 1.5,
                overflowX: 'auto',
                bgcolor: 'rgba(0,0,0,0.6)',
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': { height: 6 },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(230,184,0,0.4)', borderRadius: 3 },
                justifyContent: 'center',
              }}
            >
              {galleryImages.map((img, i) => (
                <Box
                  key={i}
                  onClick={() => setLightboxIndex(i)}
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '10px',
                    overflow: 'hidden',
                    flexShrink: 0,
                    cursor: 'pointer',
                    border: `2px solid ${i === lightboxIndex ? goldBright : 'transparent'}`,
                    opacity: i === lightboxIndex ? 1 : 0.6,
                    transition: 'all 0.2s ease',
                    '&:hover': { opacity: 1 },
                  }}
                >
                  <Box
                    component="img"
                    src={img}
                    alt=""
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Dialog>

      {/* ============ SNACKBAR ============ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{
            bgcolor: surface,
            color: '#fff',
            border: `1px solid ${border}`,
            borderRadius: '14px',
            fontWeight: 600,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

/* ============================================================
   Sub-components
   ============================================================ */

function SectionCard({ title, children, goldBright, cardBg }) {
  return (
    <Card
      sx={{
        bgcolor: cardBg,
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        border: `1px solid rgba(255,255,255,0.06)`,
        borderRadius: '20px',
        mb: 3,
        overflow: 'hidden',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: `linear-gradient(90deg, transparent, ${goldBright}66, transparent)`,
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Typography
          component="h2"
          sx={{
            color: '#fff',
            fontWeight: 800,
            fontSize: { xs: '1.05rem', sm: '1.15rem' },
            mb: 2,
            letterSpacing: -0.2,
          }}
        >
          {title}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );
}

function InfoRow({ icon, label, value, action, goldBright, isRtl }) {
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      sx={{ flexDirection: isRtl ? 'row-reverse' : 'row' }}
    >
      <Avatar
        sx={{
          width: 40,
          height: 40,
          bgcolor: 'rgba(230,184,0,0.12)',
          color: goldBright,
          '& .MuiSvgIcon-root': { fontSize: 20 },
        }}
      >
        {icon}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0, textAlign: isRtl ? 'right' : 'left' }}>
        <Typography sx={{ color: '#8A8A8A', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Typography>
        <Typography
          sx={{
            color: '#fff',
            fontSize: '0.9rem',
            fontWeight: 600,
            wordBreak: 'break-word',
          }}
        >
          {value}
        </Typography>
      </Box>
      {action}
    </Stack>
  );
}

function QuickAction({ icon, label, onClick, goldAccent, goldBright, color }) {
  return (
    <Button
      onClick={onClick}
      fullWidth
      sx={{
        flexDirection: 'column',
        gap: 0.5,
        py: 1.75,
        borderRadius: '14px',
        textTransform: 'none',
        color: color || '#fff',
        bgcolor: 'rgba(255,255,255,0.03)',
        border: `1px solid rgba(255,255,255,0.06)`,
        transition: 'all 0.2s ease',
        '& .MuiSvgIcon-root': { fontSize: 22, color: color || goldBright },
        '&:hover': {
          bgcolor: 'rgba(230,184,0,0.1)',
          borderColor: 'rgba(230,184,0,0.3)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      {icon}
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#D0D0D0' }}>
        {label}
      </Typography>
    </Button>
  );
}

function StatBox({ icon, label, value, goldBright }) {
  return (
    <Box
      sx={{
        p: 1.75,
        bgcolor: 'rgba(255,255,255,0.02)',
        border: `1px solid rgba(255,255,255,0.06)`,
        borderRadius: '14px',
        textAlign: 'center',
      }}
    >
      <Box sx={{ color: goldBright, mb: 0.5 }}>{icon}</Box>
      <Typography sx={{ color: '#8A8A8A', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ color: '#fff', fontSize: '0.95rem', fontWeight: 800 }}>
        {value}
      </Typography>
    </Box>
  );
}

export default StoreDetailPage;